/**
 * Phase 76q (Feb-2026) — Public Trip Tracking Page.
 *
 * Linked from WhatsApp completion messages as `Map:<host>/track/<trip_id>`.
 * Renders the full GPS trail of the trip on Google Maps + DC and goods
 * photos. No authentication — the trip ID UUID is the access token.
 *
 * Hits the public, unauthenticated `/api/public/trip-track/{trip_id}`
 * endpoint which is whitelisted in `_VERSION_GATE_BYPASS_PREFIXES`.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Polyline, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../lib/googleMaps';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

export default function PublicTripTrack() {
  const { tripId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const { isLoaded } = useGoogleMaps();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Public endpoint — no Authorization header, no X-App-Version.
        const { data: d } = await axios.get(`${API}/api/public/trip-track/${tripId}`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Trip not found');
      }
    })();
    return () => { cancelled = true; };
  }, [tripId]);

  const trail = useMemo(() => (data?.trail || []).map(p => ({ lat: p.lat, lng: p.lng })), [data]);
  const center = useMemo(() => {
    if (trail.length) return trail[Math.floor(trail.length / 2)];
    if (data?.trip?.pickup_lat) return { lat: data.trip.pickup_lat, lng: data.trip.pickup_lng };
    return { lat: 12.9716, lng: 77.5946 };
  }, [trail, data]);
  const trip = data?.trip;

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center" data-testid="public-track-error">
          <h1 className="text-2xl font-black text-[#0A0A0A] mb-2">Trip not found</h1>
          <p className="text-sm text-[#666]">{error}</p>
          <p className="text-xs text-[#999] mt-4">Trip ID: {tripId}</p>
        </div>
      </div>
    );
  }
  if (!data || !trip) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-sm text-[#666]">Loading trip…</p>
      </div>
    );
  }

  // Photos
  const photos = [];
  if (trip.dc_photo) photos.push({ label: 'DC Note', url: trip.dc_photo.startsWith('http') ? trip.dc_photo : `${API}${trip.dc_photo}` });
  else if (Array.isArray(trip.dc_photos) && trip.dc_photos.length > 0 && trip.dc_photos[0].url) {
    photos.push({ label: 'DC Note', url: trip.dc_photos[0].url.startsWith('http') ? trip.dc_photos[0].url : `${API}${trip.dc_photos[0].url}` });
  }
  if (trip.goods_photo) photos.push({ label: 'Goods (pickup)', url: trip.goods_photo.startsWith('http') ? trip.goods_photo : `${API}${trip.goods_photo}` });
  if (trip.unload_photo) photos.push({ label: 'Goods (drop)', url: trip.unload_photo.startsWith('http') ? trip.unload_photo : `${API}${trip.unload_photo}` });

  const totalKm = trip.google_kms || trip.traveled_km || trip.manual_kms || '-';

  return (
    <div className="min-h-screen bg-white" data-testid="public-trip-track">
      {/* Header */}
      <div className="bg-[#002FA7] text-white p-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-lg font-black tracking-widest">CARLOO · TRIP TRACK</h1>
          <p className="text-xs mt-1 opacity-90">Trip #{(trip.id || '').slice(0, 8)} · {trip.status?.toUpperCase()}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Summary */}
        <div className="border border-[#E5E5E5] p-3 grid grid-cols-2 gap-2 text-sm" data-testid="public-track-summary">
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Route</p>
            <p className="font-bold">{trip.pickup_supplier_name || '-'} → {trip.drop_supplier_name || '-'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Pilot</p>
            <p>{trip.pilot_name || '-'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Vehicle</p>
            <p>{trip.vehicle_number || '-'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Started</p>
            <p>{fmtTime(trip.started_at)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Completed</p>
            <p>{fmtTime(trip.completed_at)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Distance</p>
            <p className="font-bold">{totalKm} km</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Total Time</p>
            <p>{trip.google_total_min ? `${Math.round(trip.google_total_min)} min` : '-'}</p>
          </div>
          {trip.item && (
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-widest text-[#666]">Cargo</p>
              <p>{trip.item} · {trip.qty || ''} {trip.uom || ''}</p>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="border border-[#E5E5E5]" data-testid="public-track-map">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#666] p-2 bg-[#F8F8F8]">Live GPS Trail · {data.trail_count} points</p>
          {!isLoaded ? (
            <div className="h-[400px] flex items-center justify-center text-[#666] text-sm">Loading map…</div>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '400px' }}
              center={center}
              zoom={trail.length ? 12 : 11}
              options={{ disableDefaultUI: false, fullscreenControl: true, streetViewControl: false }}
            >
              {trail.length > 1 && (
                <Polyline
                  path={trail}
                  options={{ strokeColor: '#002FA7', strokeWeight: 4, strokeOpacity: 0.9 }}
                />
              )}
              {trip.pickup_lat && trip.pickup_lng && (
                <Marker position={{ lat: trip.pickup_lat, lng: trip.pickup_lng }} label="P" />
              )}
              {trip.drop_lat && trip.drop_lng && (
                <Marker position={{ lat: trip.drop_lat, lng: trip.drop_lng }} label="D" />
              )}
            </GoogleMap>
          )}
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="border border-[#E5E5E5] p-3" data-testid="public-track-photos">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#666] mb-2">Proof of Delivery</p>
            <div className="grid grid-cols-2 gap-2">
              {photos.map((p, i) => (
                <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="block border border-[#E5E5E5] hover:border-[#002FA7]">
                  <img src={p.url} alt={p.label} className="w-full aspect-square object-cover" />
                  <p className="text-[10px] uppercase tracking-widest text-[#666] p-1.5 text-center">{p.label}</p>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Timings */}
        <div className="border border-[#E5E5E5] p-3 text-xs" data-testid="public-track-timings">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#666] mb-2">Timings</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <p className="text-[10px] text-[#666]">Pickup wait</p>
              <p className="font-bold">{trip.google_pickup_waiting_min != null ? `${trip.google_pickup_waiting_min} min` : (trip.pickup_waiting_minutes != null ? `${trip.pickup_waiting_minutes} min` : '-')}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#666]">Drop wait</p>
              <p className="font-bold">{trip.google_drop_waiting_min != null ? `${trip.google_drop_waiting_min} min` : (trip.drop_waiting_minutes != null ? `${trip.drop_waiting_minutes} min` : '-')}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#666]">Total</p>
              <p className="font-bold">{trip.google_total_min ? `${Math.round(trip.google_total_min)} min` : '-'}</p>
            </div>
          </div>
        </div>

        <p className="text-center text-[10px] uppercase tracking-widest text-[#999] py-3">
          Powered by Carloo · emergent.carloo.in
        </p>
      </div>
    </div>
  );
}
